/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { UserPlusIcon } from "@heroicons/react/24/outline";

import AddUser from "src/components/modals/AddUser.react";
import ChangeGroup from "src/components/modals/ChangeGroup.react";
import RemoveModal from "src/components/modals/RemoveModal.react";
import UserDropdown from "src/views/settings/users/UserDropdown.react";

import { listInGroup, remove, toggleStatus } from "src/services/UserService";

/**
 * The User Management Page component
 *
 * @author Thais Watanabe
 */
const ListUsers = () => {
  const [users, setUsers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isChangeOpen, setIsChangeOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  /**
   * useEffect
   */
  useEffect(() => {
    listUsers();
  }, []);

  /**
   * listUsers
   */
  async function listUsers() {
    setUsers([]);
    var newListUsers = [];
    const groups = ["Admin", "User"];

    groups.forEach((group) => {
      listInGroup(group)
        .then((response) => {
          response.forEach((element) => (element.Group = group));
          newListUsers = newListUsers.concat(response);
          setUsers(
            newListUsers.sort((a, b) => (a.Username > b.Username ? 1 : -1))
          );
        })
        .catch((error) => {
          console.log(error);
        });
    });
  }

  /**
   * openRemoveDialog
   * @param {*} photo
   */
  async function openRemoveDialog(user) {
    setSelectedUser(user);
    setIsRemoveModalOpen(true);
  }

  /**
   * closeRemoveDialog
   */
  async function closeRemoveDialog() {
    setSelectedUser(null);
    setIsRemoveModalOpen(false);
  }

  /**
   * openChangeDialog
   * @param {*} user
   */
  function openChangeDialog(user) {
    setIsChangeOpen(!isChangeOpen);
    setSelectedUser(user);
  }

  /**
   * enableUser
   */
  async function enableUser(user) {
    await toggleStatus(user)
      .then(() => {
        listUsers();
        toast.success("保存しました");
      })
      .catch((error) => {
        console.log(error.response);
      });
  }

  /**
   * removeUser
   */
  async function removeUser() {
    await remove(selectedUser)
      .then(() => {
        listUsers();
        closeRemoveDialog();
        toast.success("ユーザーを削除しました！");
      })
      .catch((error) => {
        console.log(error.response);
      });
  }

  return (
    <>
      <section aria-labelledby="billing_history_heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
          <div className="flex items-center space-x-5">
            <div>
              <h2
                id="billing_history_heading"
                className="text-lg leading-6 font-medium text-gray-900"
              >
                ユーザー一覧
              </h2>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3">
            <button
              type="button"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
              onClick={() => setIsOpen(!isOpen)}
            >
              {/* <!-- Heroicon name: user-add  --> */}
              <UserPlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              新規ユーザー登録
            </button>
          </div>
        </div>
        <div className="mt-6 flex flex-col">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  ユーザー名
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  メールアドレス
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  グループ
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  ステータス
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length < 1 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    <div className="loader"></div>
                  </td>
                </tr>
              )}
              {users.map((user, i) => (
                <tr key={i}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.Username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.Attributes[2].Value}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.Group}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span
                      className={`${
                        user.Enabled
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      } inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium `}
                    >
                      {user.Enabled ? "有効" : "無効"}
                    </span>
                  </td>
                  <td>
                    <UserDropdown
                      user={user}
                      enableUser={enableUser}
                      openChangeDialog={openChangeDialog}
                      openRemoveDialog={openRemoveDialog}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <AddUser setIsOpen={setIsOpen} isOpen={isOpen} listUsers={listUsers} />

      <ChangeGroup
        setIsChangeOpen={setIsChangeOpen}
        isChangeOpen={isChangeOpen}
        listUsers={listUsers}
        selectedUser={selectedUser}
      />

      <RemoveModal
        open={isRemoveModalOpen}
        setOpen={setIsRemoveModalOpen}
        remove={removeUser}
      />
    </>
  );
};

export default ListUsers;
